'use client';

import { useMemo, useState } from 'react';
import { mathToolMeta, type MathToolVariant } from '@/lib/browser-math-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserMathToolkit({ variant }: { variant: MathToolVariant }) {
  const meta = useMemo(() => mathToolMeta[variant], [variant]);
  const [value, setValue] = useState(meta.default);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const output = meta.compute(Number(value));

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Enter a number and the result updates live in your browser. Nothing is uploaded and no
          server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>{meta.inputLabel}</span>
              <input
                inputMode="numeric"
                onChange={(event) => setValue(event.target.value)}
                type="number"
                value={value}
              />
            </label>
          </div>
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <textarea className="tool-live-preview-frame" readOnly rows={10} value={output} />
          </label>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? (
            <p className="status-message status-success" role="status">
              {copyMessage}
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            Calculated locally in your browser using exact big-integer math.
          </p>
        </section>
      </div>
    </div>
  );
}
